import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import { bulkPasteModelTaskQuestions } from "services/models/modelDetails/modelTasks";
import {
	getCopiedQuestionsTaskId,
	clearCopiedQuestionsClipboard,
} from "helpers/modelTaskClipboard";

/**
 * Pastes the copied task's questions onto the whole selection in one request.
 *
 * This is the loop the team ran by hand: copy questions once, then open ten or
 * twelve tasks and paste into each.
 */
const useBulkPasteQuestions = ({ modelVersionId, onPasted }) => {
	const dispatch = useDispatch();
	const [isPasting, setIsPasting] = useState(false);

	const pasteQuestions = useCallback(
		async (taskIds) => {
			const sourceTaskId = getCopiedQuestionsTaskId();
			if (!sourceTaskId) return null;

			setIsPasting(true);
			try {
				const response = await bulkPasteModelTaskQuestions({
					modelVersionId,
					sourceTaskId,
					taskIds,
				});
				if (!response?.status) {
					dispatch(
						showError(
							response?.data?.title ||
								response?.data ||
								"Could not paste questions onto the selected tasks"
						)
					);
					return null;
				}
				clearCopiedQuestionsClipboard();
				const skipped = response.data?.skippedIDs ?? [];
				if (skipped.length > 0) {
					dispatch(
						showError(`${skipped.length} of ${taskIds.length} were skipped`)
					);
				}
				await onPasted();
				return taskIds.filter((id) => !skipped.includes(id));
			} finally {
				setIsPasting(false);
			}
		},
		[dispatch, modelVersionId, onPasted]
	);

	return { pasteQuestions, isPasting };
};

export default useBulkPasteQuestions;
